import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../config/api';

const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';
const COLOR_FONDO = '#f3f4f4';
const COLOR_BURBUJA_AGENTE = '#f0f4ff';
const COLOR_ALERTA = '#dcde74';

const API_URL = `${API_BASE}/api/agente`;
const API_FOTO = `${API_BASE}/api/analizar-foto`;

const AGENTES_INFO = {
  principal: { nombre: 'Asistente Beep', endpoint: '/api/agente', etiqueta: 'Principal', color: '#5c9adb', colorFondo: '#f0f4ff' },
  comercial: { nombre: 'Axel · Comercial', endpoint: '/api/agente-comercial', etiqueta: 'Comercial', color: '#5cd4b0', colorFondo: '#f0faf6' },
  operaciones: { nombre: 'Max · Operaciones', endpoint: '/api/agente-operaciones', etiqueta: 'Operaciones', color: '#f4a261', colorFondo: '#fff8f0' },
  almacen: { nombre: 'Bruno · Almacén', endpoint: '/api/agente-almacen', etiqueta: 'Almacén', color: '#9b59b6', colorFondo: '#f9f0ff' },
  admin: { nombre: 'Nova · Administración', endpoint: '/api/agente-admin', etiqueta: 'Administración', color: '#e91e8c', colorFondo: '#fff0f8' },
  marketing: { nombre: 'Luna · Marketing', endpoint: '/api/agente-marketing', etiqueta: 'Marketing', color: '#dcde74', colorFondo: '#fafff0' },
  proyectos: { nombre: 'Rex · Proyectos', endpoint: '/api/agente-proyectos', etiqueta: 'Proyectos', color: '#5cd4b0', colorFondo: '#f0faf6' },
};
const KM_KEY = 'beep_kilometraje_unidades';
const ALERTAS_KEY = 'beep_alertas_supervisor';

function horaAhora() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
}

function saludoHora() {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

function bienvenidaPorRol(rol, nombre, contexto = {}) {
  const saludo = `${saludoHora()} ${nombre || ''}`.trim() + '.';
  const r = (rol || '').toLowerCase();
  if (r.includes('direc')) {
    const pend = contexto.pendientesAutorizar ?? 0;
    const serv = contexto.serviciosActivos ?? 0;
    return `${saludo} Tienes ${pend} pendientes por autorizar y ${serv} servicios activos hoy.`;
  }
  if (r.includes('tec') || r.includes('téc')) {
    const serv = contexto.serviciosHoy ?? 0;
    return `${saludo} Hoy tienes ${serv} servicios programados.`;
  }
  if (r.includes('comer') || r.includes('vent')) {
    const leads = contexto.leadsPendientes ?? 0;
    return `${saludo} Tienes ${leads} leads pendientes de contactar.`;
  }
  return `${saludo} ¿En qué puedo ayudarte hoy?`;
}

function sugerenciasPorRol(rol) {
  const r = (rol || '').toLowerCase();
  if (r.includes('direc')) {
    return ['Ver pendientes', 'Resumen del día', '¿Quién faltó hoy?'];
  }
  if (r.includes('tec') || r.includes('téc')) {
    return ['Mis servicios de hoy', 'Registrar gasto', 'Reportar problema'];
  }
  if (r.includes('comer') || r.includes('vent')) {
    return ['Mis leads', 'Nueva cotización', 'Ver clientes'];
  }
  return ['Mis tareas', 'Nuevo requerimiento', 'Mi día'];
}

function AgenteBeep({ usuario, rol, nombre: nombreProp, onNavegar, onAccion, onCerrar, esMovil }) {
  const nombre =
    nombreProp ||
    (usuario && (usuario.nombre || usuario.name)) ||
    (typeof usuario === 'string' ? usuario : '') ||
    'Usuario';
  const [mensajes, setMensajes] = useState([]);
  const [input, setInput] = useState('');
  const [escribiendo, setEscribiendo] = useState(false);
  const [grabando, setGrabando] = useState(false);
  const [confirmacion, setConfirmacion] = useState(null);
  const [esperandoKm, setEsperandoKm] = useState(null); // {datosFoto}
  const [agenteActivo, setAgenteActivo] = useState('principal');
  const scrollRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const pendingAgenteRef = useRef(null);
  const mountedRef = useRef(false);

  const agregarMensajeAgente = (texto, agenteId) => {
    const ag = agenteId || agenteActivo;
    setMensajes((prev) => [
      ...prev,
      { autor: 'agente', texto, hora: horaAhora(), agente: ag, agenteColor: AGENTES_INFO[ag] },
    ]);
  };

  const formatoFechaHora = (d) => {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return { fecha: `${dd}/${mm}/${yyyy}`, hora: `${hh}:${mi}` };
  };

  const abrirCamara = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const guardarAlerta = (alerta) => {
    try {
      const raw = localStorage.getItem(ALERTAS_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      arr.push(alerta);
      localStorage.setItem(ALERTAS_KEY, JSON.stringify(arr));
    } catch {}
  };

  const onSeleccionarFoto = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const img = new Image();
    img.onload = () => {
      if (img.width > img.height) {
        agregarMensajeAgente(
          'Por favor toma la foto en vertical (modo retrato). Las fotos horizontales no son aceptadas en el sistema.'
        );
        URL.revokeObjectURL(img.src);
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const dataUrl = reader.result;
        const fecha = new Date();
        // Mostrar foto del usuario y mensaje "Analizando..."
        setMensajes((prev) => [
          ...prev,
          {
            autor: 'usuario',
            tipo: 'foto',
            imagen: dataUrl,
            hora: horaAhora(),
          },
          {
            autor: 'agente',
            texto: 'Analizando foto... 🔍',
            hora: horaAhora(),
          },
        ]);

        try {
          const res = await fetch(API_FOTO, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imagen: dataUrl, tipo: 'gasto' }),
          });
          const result = await res.json();

          if (!result.aprobada) {
            setMensajes((prev) => [
              ...prev,
              {
                autor: 'agente',
                tipo: 'card',
                variante: 'error',
                titulo: '❌ Foto rechazada',
                cuerpo: result.motivo_rechazo || 'No se pudo validar la foto.',
                extra: 'Por favor toma una nueva foto del comprobante.',
                hora: horaAhora(),
              },
            ]);
            if (result.es_de_hoy === false) {
              guardarAlerta({
                tipo: 'foto_no_es_de_hoy',
                descripcion: `Foto de comprobante con fecha ${result.fecha_ticket || 'desconocida'} (no es de hoy)`,
                usuario: nombre,
                fecha: formatoFechaHora(new Date()).fecha,
                hora: formatoFechaHora(new Date()).hora,
              });
            }
            return;
          }

          // Foto aprobada
          setMensajes((prev) => [
            ...prev,
            {
              autor: 'agente',
              tipo: 'card',
              variante: 'exito',
              titulo: '✅ Foto válida',
              lineas: [
                result.monto && `💰 Monto: $${result.monto}`,
                result.litros && `⛽ Litros: ${result.litros}`,
                result.fecha_ticket && `📅 Fecha: ${result.fecha_ticket}`,
                result.estacion && `🏪 Estación: ${result.estacion}`,
              ].filter(Boolean),
              hora: horaAhora(),
            },
            {
              autor: 'agente',
              texto: '¿Cuál es el kilometraje actual de la unidad?',
              hora: horaAhora(),
            },
          ]);
          setEsperandoKm({ datosFoto: result, fecha, dataUrl });
        } catch (err) {
          agregarMensajeAgente('Error al analizar la foto. Intenta de nuevo.');
        }
      };
      reader.readAsDataURL(file);
    };
    img.src = URL.createObjectURL(file);
  };

  const procesarKilometraje = (textoUsuario) => {
    const num = parseInt(String(textoUsuario).replace(/[^0-9]/g, ''), 10);
    if (!num || isNaN(num)) {
      agregarMensajeAgente('No entendí el kilometraje. Por favor envía solo el número.');
      return true;
    }
    const unidad = 'unidad_default';
    let registros = {};
    try {
      const raw = localStorage.getItem(KM_KEY);
      registros = raw ? JSON.parse(raw) : {};
    } catch {}
    const ultimo = registros[unidad];
    const ahora = new Date();
    const { fecha: fHoy, hora: hHoy } = formatoFechaHora(ahora);

    if (ultimo && num <= ultimo.km) {
      const fechaAnt = ultimo.fecha;
      setMensajes((prev) => [
        ...prev,
        {
          autor: 'agente',
          tipo: 'card',
          variante: 'error',
          titulo: '⚠️ ALERTA',
          cuerpo: `El kilometraje ingresado (${num.toLocaleString('es-MX')}) es menor al último registrado (${ultimo.km.toLocaleString('es-MX')}) el ${fechaAnt} por ${ultimo.registradoPor}. Este intento quedará registrado.`,
          hora: horaAhora(),
        },
      ]);
      guardarAlerta({
        tipo: 'kilometraje_menor',
        descripcion: `Intento de registrar km ${num} (anterior: ${ultimo.km}, registrado por ${ultimo.registradoPor} el ${fechaAnt})`,
        usuario: nombre,
        fecha: fHoy,
        hora: hHoy,
      });
      setEsperandoKm(null);
      return true;
    }

    // Aceptado
    registros[unidad] = {
      km: num,
      fecha: fHoy,
      registradoPor: nombre,
    };
    try {
      localStorage.setItem(KM_KEY, JSON.stringify(registros));
    } catch {}

    const datos = esperandoKm.datosFoto || {};
    setMensajes((prev) => [
      ...prev,
      {
        autor: 'agente',
        tipo: 'card',
        variante: 'exito',
        titulo: '✅ Gasto registrado exitosamente',
        lineas: [
          `👤 ${nombre}`,
          `📅 ${fHoy} ${hHoy}`,
          `⛽ ${datos.tipo_gasto || 'Gasolina'}: $${datos.monto || '—'}${datos.litros ? ` — ${datos.litros} litros` : ''}`,
          `🚗 Kilometraje: ${num.toLocaleString('es-MX')} km`,
          datos.estacion && `📍 ${datos.estacion}`,
        ].filter(Boolean),
        hora: horaAhora(),
      },
    ]);
    setEsperandoKm(null);
    return true;
  };

  // Mensaje de bienvenida
  useEffect(() => {
    setMensajes([
      {
        autor: 'agente',
        texto: `Hola ${nombre} 👋 Soy Beep, tu asistente de Beep México. ¿En qué te ayudo hoy?`,
        hora: horaAhora(),
        sugerencias: sugerenciasPorRol(rol),
        agente: 'principal',
        agenteColor: AGENTES_INFO.principal,
      },
    ]);
    // eslint-disable-next-line
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mensajes, escribiendo]);

  useEffect(() => {
    mountedRef.current = true;
    if (pendingAgenteRef.current) {
      const agenteId = pendingAgenteRef.current;
      pendingAgenteRef.current = null;
      if (!agenteId || agenteId === 'beep') {
        setAgenteActivo('principal');
        agregarMensajeAgente('Hola 👋 Soy Beep. ¿En qué te ayudo?', 'principal');
      } else if (AGENTES_INFO[agenteId]) {
        setAgenteActivo(agenteId);
        const info = AGENTES_INFO[agenteId];
        const primerNombre = info.nombre.split(' · ')[0];
        agregarMensajeAgente(`Hola 👋 Soy ${primerNombre}. ¿En qué te puedo ayudar?`, agenteId);
      }
    }
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const agenteId = e.detail;
      if (!mountedRef.current) {
        pendingAgenteRef.current = agenteId;
        return;
      }
      if (!agenteId || agenteId === 'beep') {
        setAgenteActivo('principal');
        agregarMensajeAgente('Hola 👋 Soy Beep. ¿En qué te ayudo?', 'principal');
      } else if (AGENTES_INFO[agenteId]) {
        setAgenteActivo(agenteId);
        const info = AGENTES_INFO[agenteId];
        const primerNombre = info.nombre.split(' · ')[0];
        agregarMensajeAgente(`Hola 👋 Soy ${primerNombre}. ¿En qué te puedo ayudar?`, agenteId);
      }
    };
    window.addEventListener('beep:cambiar-agente', handler);
    return () => window.removeEventListener('beep:cambiar-agente', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ejecutarAccion = (accion) => {
    if (!accion || !accion.accion) return;
    if (accion.accion === 'derivar_agente' && accion.agente && AGENTES_INFO[accion.agente]) {
      const info = AGENTES_INFO[accion.agente];
      setAgenteActivo(accion.agente);
      agregarMensajeAgente(`Te conecto con nuestro ${info.nombre}...`);
      return;
    }
    if (accion.accion === 'navegar' && accion.ruta) {
      if (onNavegar) onNavegar(accion.ruta, accion.tab || null);
    } else if (accion.accion === 'confirmar_accion') {
      setConfirmacion(accion);
    } else if (accion.accion === 'mostrar_info' || accion.accion === 'mostrar_resumen') {
      setMensajes((prev) => [
        ...prev,
        {
          autor: 'agente',
          tipo: 'card',
          datos: accion,
          hora: horaAhora(),
        },
      ]);
    } else {
      if (onAccion) onAccion(accion);
    }
  };

  const enviarMensaje = async (textoForzado) => {
    const texto = (textoForzado ?? input).trim();
    if (!texto || escribiendo) return;
    if (esperandoKm && textoForzado === undefined) {
      setMensajes((prev) => [
        ...prev,
        { autor: 'usuario', texto, hora: horaAhora() },
      ]);
      setInput('');
      procesarKilometraje(texto);
      return;
    }
    const nuevoUsuario = { autor: 'usuario', texto, hora: horaAhora() };
    const historial = [...mensajes, nuevoUsuario];
    setMensajes(historial);
    setInput('');
    setEscribiendo(true);
    try {
      const endpoint = `${API_BASE}${AGENTES_INFO[agenteActivo].endpoint}`;
      // FASE 1: /api/agente exige authBearer en backend. Otros endpoints
      // de agentes aun no lo exigen pero mandar el token no rompe nada.
      const token = localStorage.getItem('beep_token');
      // FIX-2X: zona horaria del navegador para que el agente conozca la
      // hora local del usuario y no la del servidor (UTC).
      let tz;
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = null; }
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          mensaje: texto,
          historial: mensajes.filter((m) => m.autor !== 'sistema'),
          rol: rol || 'Usuario',
          nombre: nombre || 'Usuario',
          contexto: {},
          tz,
        }),
      });
      const data = await res.json();
      const respuesta =
        data.respuesta || 'No pude procesar tu mensaje en este momento.';
      setMensajes((prev) => [
        ...prev,
        { autor: 'agente', texto: respuesta, hora: horaAhora(), agente: agenteActivo, agenteColor: AGENTES_INFO[agenteActivo] },
      ]);
      if (data.accion) {
        setTimeout(() => ejecutarAccion(data.accion), 1000);
      }
    } catch (err) {
      setMensajes((prev) => [
        ...prev,
        {
          autor: 'agente',
          texto: 'Hubo un error al conectar con el servidor. Intenta de nuevo.',
          hora: horaAhora(),
        },
      ]);
    } finally {
      setEscribiendo(false);
    }
  };

  const iniciarDictado = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert('Tu navegador no soporta reconocimiento de voz.');
      return;
    }
    if (grabando && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }
    const rec = new SR();
    rec.lang = 'es-MX';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setInput((prev) => (prev ? prev + ' ' + t : t));
    };
    rec.onend = () => setGrabando(false);
    rec.onerror = () => setGrabando(false);
    recognitionRef.current = rec;
    setGrabando(true);
    rec.start();
  };

  const contenedorStyle = esMovil
    ? {
        position: 'fixed',
        inset: 0,
        background: '#fff',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 400,
        background: '#fff',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.18)',
      };

  return (
    <div style={contenedorStyle}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          borderBottom: `1px solid ${COLOR_PRINCIPAL}22`,
          background: '#fff',
        }}
      >
        <img
          src="/icono azul beep.png"
          alt="Beep"
          style={{ width: 36, height: 36, objectFit: 'contain' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: COLOR_OSCURO }}>
            {AGENTES_INFO[agenteActivo].nombre}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#666',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: COLOR_VERDE,
              }}
            />
            {agenteActivo === 'principal' ? 'En línea' : `Especialista · ${AGENTES_INFO[agenteActivo].etiqueta}`}
          </div>
        </div>
        {agenteActivo !== 'principal' && (
          <button
            onClick={() => {
              setAgenteActivo('principal');
              agregarMensajeAgente('Volviste al Asistente Principal. ¿En qué más puedo ayudarte?');
            }}
            title="Volver al Asistente Principal"
            style={{
              background: COLOR_BURBUJA_AGENTE,
              border: `1px solid ${COLOR_PRINCIPAL}55`,
              color: COLOR_OSCURO,
              borderRadius: 14,
              padding: '4px 10px',
              fontSize: 11,
              cursor: 'pointer',
              marginRight: 4,
              whiteSpace: 'nowrap',
            }}
          >
            ← Beep
          </button>
        )}
        <button
          onClick={onCerrar}
          aria-label="Cerrar"
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 22,
            color: '#666',
            cursor: 'pointer',
            padding: 4,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>

      {/* Mensajes */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          background: COLOR_FONDO,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {mensajes.map((m, i) => {
          if (m.autor === 'usuario' && m.tipo === 'foto') {
            return (
              <div
                key={i}
                style={{
                  alignSelf: 'flex-end',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: 4,
                }}
              >
                <img
                  src={m.imagen}
                  alt="foto"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: `2px solid ${COLOR_PRINCIPAL}`,
                  }}
                />
<span style={{ fontSize: 10, color: '#888' }}>{m.hora}</span>
              </div>
            );
          }
          if (m.autor === 'usuario') {
            return (
              <div
                key={i}
                style={{
                  alignSelf: 'flex-end',
                  maxWidth: '80%',
                  background: COLOR_PRINCIPAL,
                  color: '#fff',
                  padding: '10px 14px',
                  borderRadius: '14px 14px 4px 14px',
                  fontSize: 14,
                  wordBreak: 'break-word',
                }}
              >
                <div>{m.texto}</div>
                <div
                  style={{
                    fontSize: 10,
                    opacity: 0.8,
                    marginTop: 4,
                    textAlign: 'right',
                  }}
                >
                  {m.hora}
                </div>
              </div>
            );
          }
          if (m.tipo === 'card') {
            const esError = m.variante === 'error';
            const esExito = m.variante === 'exito';
            const bg = esError ? '#fdecec' : esExito ? '#eafaf3' : '#fff';
            const borde = esError ? COLOR_ROJO : esExito ? COLOR_VERDE : COLOR_PRINCIPAL;
            const titColor = esError ? COLOR_ROJO : esExito ? '#1b8a5a' : COLOR_OSCURO;
            return (
              <div
                key={i}
                style={{
                  alignSelf: 'flex-start',
                  maxWidth: '90%',
                  background: bg,
                  border: `1.5px solid ${borde}`,
                  borderRadius: 12,
                  padding: 12,
                  fontSize: 13,
                  color: '#222',
                }}
              >
                {m.titulo && (
                  <div style={{ fontWeight: 700, color: titColor, marginBottom: 6 }}>
                    {m.titulo}
                  </div>
                )}
                {m.cuerpo && (
                  <div style={{ marginBottom: m.extra ? 6 : 0 }}>{m.cuerpo}</div>
                )}
                {m.extra && <div style={{ fontSize: 12, color: '#666' }}>{m.extra}</div>}
                {m.lineas && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {m.lineas.map((l, k) => (
                      <div key={k}>{l}</div>
                    ))}
                  </div>
                )}
                {m.datos && !m.titulo && (
                  <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                    {JSON.stringify(m.datos, null, 2)}
                  </pre>
                )}
                <div style={{ fontSize: 10, color: '#888', marginTop: 6 }}>{m.hora}</div>
              </div>
            );
          }
          return (
            <div
              key={i}
              style={{
                alignSelf: 'flex-start',
                maxWidth: '85%',
                background: m.agenteColor?.colorFondo || COLOR_BURBUJA_AGENTE,
                borderLeft: `3px solid ${m.agenteColor?.color || COLOR_PRINCIPAL}`,
                color: '#222',
                padding: '10px 14px',
                borderRadius: '4px 14px 14px 14px',
                fontSize: 14,
                wordBreak: 'break-word',
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{m.texto}</div>
              <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                {m.hora}
              </div>
              {m.sugerencias && m.sugerencias.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    marginTop: 8,
                  }}
                >
                  {m.sugerencias.map((s, j) => (
                    <button
                      key={j}
                      onClick={() => enviarMensaje(s)}
                      style={{
                        background: '#fff',
                        border: `1px solid ${COLOR_PRINCIPAL}77`,
                        color: COLOR_OSCURO,
                        borderRadius: 16,
                        padding: '6px 12px',
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {escribiendo && (
          <div
            style={{
              alignSelf: 'flex-start',
              background: COLOR_BURBUJA_AGENTE,
              borderLeft: `3px solid ${COLOR_PRINCIPAL}`,
              padding: '12px 16px',
              borderRadius: '4px 14px 14px 14px',
              display: 'flex',
              gap: 4,
            }}
          >
            <span className="beep-dot" style={dotStyle(0)} />
            <span className="beep-dot" style={dotStyle(0.15)} />
            <span className="beep-dot" style={dotStyle(0.3)} />
            <style>{`
              @keyframes beepDot {
                0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
                30% { opacity: 1; transform: translateY(-3px); }
              }
            `}</style>
          </div>
        )}

        {confirmacion && (
          <div
            style={{
              alignSelf: 'flex-start',
              maxWidth: '90%',
              background: '#fff',
              border: `1px solid ${COLOR_PRINCIPAL}55`,
              borderRadius: 12,
              padding: 12,
              fontSize: 13,
            }}
          >
            <div style={{ marginBottom: 10, color: '#222' }}>
              {confirmacion.texto || '¿Confirmas esta acción?'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => {
                  if (confirmacion.siguiente) ejecutarAccion(confirmacion.siguiente);
                  setConfirmacion(null);
                }}
                style={{
                  background: COLOR_VERDE,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Sí
              </button>
              <button
                onClick={() => setConfirmacion(null)}
                style={{
                  background: COLOR_ROJO,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                No
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          padding: 12,
          borderTop: `1px solid ${COLOR_PRINCIPAL}22`,
          background: '#fff',
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && enviarMensaje()}
          placeholder="Escribe tu mensaje..."
          style={{
            flex: 1,
            minWidth: 0,
            border: `1px solid ${COLOR_PRINCIPAL}55`,
            borderRadius: 20,
            padding: '10px 14px',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={onSeleccionarFoto}
        />
        <button
          onClick={abrirCamara}
          title="Tomar foto"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            background: COLOR_PRINCIPAL,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          📷
        </button>
        <button
          onClick={iniciarDictado}
          title="Dictar"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            background: grabando ? COLOR_ROJO : COLOR_PRINCIPAL,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          🎤
        </button>
        <button
          onClick={() => enviarMensaje()}
          disabled={!input.trim() || escribiendo}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            background: COLOR_OSCURO,
            color: '#fff',
            cursor: input.trim() && !escribiendo ? 'pointer' : 'not-allowed',
            opacity: input.trim() && !escribiendo ? 1 : 0.5,
            fontSize: 16,
            flexShrink: 0,
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}

const dotStyle = (delay) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: COLOR_PRINCIPAL,
  animation: `beepDot 1.2s infinite ${delay}s`,
  display: 'inline-block',
});

export default AgenteBeep;
